import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '../lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token');
  const isInvite = searchParams.get('type') === 'invite';

  useEffect(() => {
    if (!token) {
      setError(
        isInvite
          ? 'Invalid invite link. Please ask your administrator to resend the invitation.'
          : 'Invalid reset link. Please request a new password reset.',
      );
    }
  }, [token, isInvite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(
        isInvite
          ? 'Invalid invite link. Please ask your administrator to resend the invitation.'
          : 'Invalid reset link. Please request a new password reset.',
      );
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
        (err as Error)?.message ||
        (isInvite ? 'Failed to set password' : 'Failed to reset password');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = isInvite ? 'Set Your Password' : 'Reset Password';
  const submitLabel = isInvite
    ? loading
      ? 'Saving...'
      : 'Set Password'
    : loading
      ? 'Resetting...'
      : 'Reset Password';
  const successMessage = isInvite
    ? 'Your password has been set. Redirecting to login page...'
    : 'Password has been reset successfully. Redirecting to login page...';

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-3xl">{pageTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                {isInvite
                  ? 'Invalid invite link. Please ask your administrator to resend the invitation.'
                  : 'Invalid reset link. Please request a new password reset.'}
              </AlertDescription>
            </Alert>
            <div className="text-center mt-4">
              {!isInvite && (
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  Request Password Reset
                </Link>
              )}
              {isInvite && (
                <Link to="/login" className="text-sm text-primary hover:underline">
                  Go to Sign in
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-3xl">{pageTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <Alert>
                <AlertDescription>{successMessage}</AlertDescription>
              </Alert>
              <div className="text-center">
                <Link to="/login" className="text-sm text-primary hover:underline">
                  Go to Sign in
                </Link>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {isInvite && (
                <p className="text-sm text-muted-foreground text-center">
                  Welcome to Urban Logix. Choose a password to activate your account.
                </p>
              )}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{isInvite ? 'Password' : 'New Password'}</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    placeholder="Password (min 8 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full"
                >
                  {submitLabel}
                </Button>
              </div>

              <div className="text-center">
                <Link to="/login" className="text-sm text-primary hover:underline">
                  Back to Sign in
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
